import { createBrowserHistory } from "history";

const history = createBrowserHistory();

function cloneLocation(location = history.location) {
  return {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    state: location.state,
  };
}

/**
 * Intercepta o back do browser/Android (`popstate`) e o botão físico no
 * Cordova/WebView (`backbutton`) enquanto o retorno desta função não for chamado.
 *
 * Empilha uma cópia da entrada atual para o primeiro back não desmontar a tela.
 * Se `onBack` resolver para falsy (continuar), recoloca a entrada no histórico.
 * Se resolver para truthy, assume que `onBack` já navegou (ex.: `/daily`).
 *
 * Não deve ser registrado fora da tela que precisa do aviso.
 *
 * @param {() => boolean | Promise<boolean>} onBack
 * @returns {() => void}
 */
export function interceptBack(onBack) {
  const guardedLocation = cloneLocation();
  let active = true;
  let handling = false;
  let restoring = false;
  let consumedByPop = false;

  history.push(guardedLocation);

  const restoreGuardedEntry = () => {
    if (!active) return;

    const current = cloneLocation();
    const leftTheScreen = current.pathname !== guardedLocation.pathname;
    if (!leftTheScreen && !consumedByPop) {
      return;
    }

    restoring = true;
    consumedByPop = false;
    try {
      history.push(cloneLocation(guardedLocation));
    } finally {
      restoring = false;
    }
  };

  const handleBack = async () => {
    if (!active || handling || restoring) return;
    handling = true;
    try {
      const shouldLeave = await onBack();
      if (!shouldLeave && active) {
        restoreGuardedEntry();
      }
    } finally {
      handling = false;
    }
  };

  const onPopState = () => {
    if (!active || restoring) return;
    consumedByPop = true;
    handleBack();
  };

  const onBackButton = (event) => {
    if (!active) return;
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    handleBack();
  };

  window.addEventListener("popstate", onPopState);
  document.addEventListener("backbutton", onBackButton, false);

  return () => {
    active = false;
    window.removeEventListener("popstate", onPopState);
    document.removeEventListener("backbutton", onBackButton, false);
  };
}

export default history;
